import { useState } from 'react';
import { Plus, Search, Archive, Trash2, Edit2, ChevronDown, ChevronRight, Info } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { mockGlobalCategories, mockGlobalSubcategories, mockCategoryStats } from '@/lib/mock-data/category-mock-data';
import type { GlobalCategory, GlobalSubcategory } from '@/types/category.types';

export default function CategoryManagement() {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState<GlobalCategory | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const filteredCategories = mockGlobalCategories.filter(category => {
    const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         category.code?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || category.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const toggleCategory = (categoryId: string) => {
    const updated = new Set(expandedCategories);
    if (updated.has(categoryId)) {
      updated.delete(categoryId);
    } else {
      updated.add(categoryId);
    }
    setExpandedCategories(updated);
  };

  const getCategoryTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      labour: 'Labour',
      expenses: 'Expenses',
      subcontractors: 'Subcontractors',
      overhead: 'Overhead',
      contingency: 'Contingency',
    };
    return labels[type] || type;
  };

  const getCategoryTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      labour: 'bg-blue-100 text-blue-800',
      expenses: 'bg-green-100 text-green-800',
      subcontractors: 'bg-purple-100 text-purple-800',
      overhead: 'bg-orange-100 text-orange-800',
      contingency: 'bg-amber-100 text-amber-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const handleDeleteCategory = (category: GlobalCategory) => {
    setSelectedCategory(category);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    console.log('Deleting category:', selectedCategory?.id);
    setShowDeleteDialog(false);
    setSelectedCategory(null);
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Budget Category Management</h1>
          <p className="text-muted-foreground mt-1">
            Manage global budget categories and subcategories used across all projects
          </p>
        </div>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Category
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Categories
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockCategoryStats.totalGlobal}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Active
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {mockCategoryStats.totalActive}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Archived
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-500">
              {mockCategoryStats.totalArchived}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Most Used
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium">
              {mockCategoryStats.mostUsedCategories[0]?.name}
            </div>
            <div className="text-xs text-muted-foreground">
              {mockCategoryStats.mostUsedCategories[0]?.usageCount} projects
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-900">
                Global Categories vs Project-Specific Categories
              </p>
              <p className="text-sm text-blue-800">
                <strong>Global categories</strong> are managed here and available across all projects (read-only in budget editing).
                Projects can also create <strong>project-specific categories</strong> for unique requirements (editable in budget editing).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle>Categories</CardTitle>
          <CardDescription>
            Manage your organization's budget category taxonomy
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search categories by name or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="labour">Labour</SelectItem>
                <SelectItem value="expenses">Expenses</SelectItem>
                <SelectItem value="subcontractors">Subcontractors</SelectItem>
                <SelectItem value="overhead">Overhead</SelectItem>
                <SelectItem value="contingency">Contingency</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Categories List */}
          <div className="space-y-2">
            {filteredCategories.map((category) => {
              const subcategories = mockGlobalSubcategories.filter(
                s => s.categoryId === category.id
              );
              const isExpanded = expandedCategories.has(category.id);

              return (
                <div key={category.id} className="border rounded-lg">
                  <div className="p-4 flex items-center gap-3 hover:bg-muted/50">
                    {subcategories.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => toggleCategory(category.id)}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                    {subcategories.length === 0 && <div className="w-8" />}

                    <div className="flex-1 grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-4">
                        <div className="font-medium">{category.name}</div>
                        {category.code && (
                          <div className="text-xs text-muted-foreground">{category.code}</div>
                        )}
                      </div>
                      <div className="col-span-3">
                        <Badge className={getCategoryTypeColor(category.type)}>
                          {getCategoryTypeLabel(category.type)}
                        </Badge>
                      </div>
                      <div className="col-span-2 text-sm text-muted-foreground">
                        {category.usageCount} projects
                      </div>
                      <div className="col-span-2">
                        {category.isDefault && (
                          <Badge variant="outline" className="text-xs">
                            Default
                          </Badge>
                        )}
                      </div>
                      <div className="col-span-1 flex justify-end gap-1">
                        <Button variant="ghost" size="sm">
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCategory(category)}
                          disabled={category.usageCount > 0}
                        >
                          {category.usageCount > 0 ? (
                            <Archive className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Trash2 className="h-4 w-4 text-destructive" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Subcategories */}
                  {isExpanded && subcategories.length > 0 && (
                    <div className="border-t bg-muted/30">
                      {subcategories.map((sub) => (
                        <div
                          key={sub.id}
                          className="p-3 pl-16 flex items-center gap-3 hover:bg-muted/50"
                        >
                          <div className="flex-1 grid grid-cols-12 gap-4 items-center">
                            <div className="col-span-4">
                              <div className="text-sm font-medium">{sub.name}</div>
                              {sub.code && (
                                <div className="text-xs text-muted-foreground">{sub.code}</div>
                              )}
                            </div>
                            <div className="col-span-3">
                              <span className="text-sm text-muted-foreground">Subcategory</span>
                            </div>
                            <div className="col-span-2 text-sm text-muted-foreground">
                              {sub.usageCount} projects
                            </div>
                            <div className="col-span-2">
                              {sub.isDefault && (
                                <Badge variant="outline" className="text-xs">
                                  Default
                                </Badge>
                              )}
                            </div>
                            <div className="col-span-1 flex justify-end gap-1">
                              <Button variant="ghost" size="sm">
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={sub.usageCount > 0}
                              >
                                {sub.usageCount > 0 ? (
                                  <Archive className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {filteredCategories.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <p>No categories found matching your search.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {selectedCategory?.usageCount === 0 ? 'Delete Category' : 'Archive Category'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedCategory?.usageCount === 0 ? (
                <>
                  Are you sure you want to delete <strong>{selectedCategory?.name}</strong>?
                  This action cannot be undone.
                </>
              ) : (
                <>
                  <strong>{selectedCategory?.name}</strong> is currently used in{' '}
                  {selectedCategory?.usageCount} project(s). You cannot delete it, but you can
                  archive it to prevent it from being used in new projects.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              {selectedCategory?.usageCount === 0 ? 'Delete' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
